import { CLIP_FORMATS, type ClipFormat } from "@kmstories/shared";
import { useEffect, useState, type FormEvent } from "react";
import { Bdi, Ltr } from "../../components/Bdi.js";
import { useToast } from "../../components/Toast.js";
import { useT } from "../../i18n/LocaleProvider.js";
import { api, ApiError } from "../../lib/api.js";

interface ClipRow {
  id: string;
  slug: string;
  youtube_id: string;
  format: ClipFormat;
  title_en: string;
  title_fa: string;
  caption_en: string;
  caption_fa: string;
  description_en: string | null;
  description_fa: string | null;
  published: boolean;
  shares: number;
  created_at: string;
}

const empty = { slug: "", youtubeId: "", format: "video" as ClipFormat, titleEn: "", titleFa: "", captionEn: "", captionFa: "", descriptionEn: "", descriptionFa: "", published: false };

/** Clips are REAL YouTube videos/Shorts published by the team — never youtube.com/clip/ URLs. */
export function AdminClips() {
  const { m, lp } = useT();
  const toast = useToast();
  const [clips, setClips] = useState<ClipRow[]>([]);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = () => api.get<{ clips: ClipRow[] }>("/api/admin/clips").then((r) => setClips(r.clips)).catch(() => {});
  useEffect(() => {
    void load();
  }, []);

  async function create(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post("/api/admin/clips", {
        ...form,
        descriptionEn: form.descriptionEn || undefined,
        descriptionFa: form.descriptionFa || undefined,
      });
      setForm(empty);
      await load();
      toast(m.withdraw.saved);
    } catch (err) {
      setError(err instanceof ApiError ? `${err.code}${err.issues.length ? ": " + err.issues.map((i) => `${i.path} ${i.message}`).join(", ") : ""}` : m.common.error);
    } finally {
      setBusy(false);
    }
  }

  const set = (k: keyof typeof empty) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm({ ...form, [k]: e.target.type === "checkbox" ? (e.target as HTMLInputElement).checked : e.target.value });

  return (
    <div className="form">
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>slug</th>
              <th>YouTube</th>
              <th>{m.share.titleLabel}</th>
              <th>published</th>
              <th>shares</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {clips.map((c) => (
              <tr key={c.id}>
                <td>
                  <a href={lp(`/c/${c.slug}`)} target="_blank" rel="noopener noreferrer"><Ltr>{c.slug}</Ltr></a>
                </td>
                <td><Ltr>{c.youtube_id}</Ltr> <span className="badge">{c.format}</span></td>
                <td>
                  <div>{c.title_en}</div>
                  <div dir="rtl"><Bdi>{c.title_fa}</Bdi></div>
                </td>
                <td>
                  <label className="small">
                    <input type="checkbox" checked={c.published} onChange={(e) => api.patch(`/api/admin/clips/${c.id}`, { published: e.target.checked }).then(load)} /> {c.published ? "yes" : "no"}
                  </label>
                </td>
                <td>{c.shares}</td>
                <td className="small">
                  <button type="button" className="linkish" onClick={() => api.post(`/api/admin/clips/${c.id}/og-image`, {}).then(() => toast("og regenerated"))}>
                    og
                  </button>{" "}
                  <button type="button" className="linkish" onClick={() => { if (window.confirm(m.admin.delete)) void api.delete(`/api/admin/clips/${c.id}`).then(load); }}>
                    {m.admin.delete}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {clips.length === 0 && <p className="muted">{m.admin.empty}</p>}
      </div>

      <form className="card form" onSubmit={create}>
        <h2 style={{ fontSize: "1.1rem", margin: 0 }}>+ {m.admin.clips}</h2>
        <div className="grid-3">
          <label className="field"><span className="field__label">slug</span><input className="input" dir="ltr" required pattern="[a-z0-9]+(-[a-z0-9]+)*" value={form.slug} onChange={set("slug")} /></label>
          <label className="field"><span className="field__label">YouTube video id (11 chars)</span><input className="input" dir="ltr" required pattern="[A-Za-z0-9_-]{11}" value={form.youtubeId} onChange={set("youtubeId")} /></label>
          <label className="field"><span className="field__label">format</span>
            <select className="select" value={form.format} onChange={set("format")}>
              {CLIP_FORMATS.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </label>
        </div>
        <div className="grid-2">
          <label className="field"><span className="field__label">Title (EN)</span><input className="input" required value={form.titleEn} onChange={set("titleEn")} /></label>
          <label className="field"><span className="field__label">عنوان (FA)</span><input className="input" dir="rtl" lang="fa" required value={form.titleFa} onChange={set("titleFa")} /></label>
          <label className="field"><span className="field__label">Caption (EN, ≤200)</span><textarea className="textarea" style={{ minHeight: "5rem" }} required maxLength={280} value={form.captionEn} onChange={set("captionEn")} /></label>
          <label className="field"><span className="field__label">متن پست (FA)</span><textarea className="textarea" style={{ minHeight: "5rem" }} dir="rtl" lang="fa" required maxLength={280} value={form.captionFa} onChange={set("captionFa")} /></label>
          <label className="field"><span className="field__label">Description (EN)</span><textarea className="textarea" style={{ minHeight: "5rem" }} value={form.descriptionEn} onChange={set("descriptionEn")} /></label>
          <label className="field"><span className="field__label">توضیح (FA)</span><textarea className="textarea" style={{ minHeight: "5rem" }} dir="rtl" lang="fa" value={form.descriptionFa} onChange={set("descriptionFa")} /></label>
        </div>
        <label className="check"><input type="checkbox" checked={form.published} onChange={set("published")} /><span>published</span></label>
        {error && <p className="field__error" role="alert">{error}</p>}
        <div className="actions" style={{ justifyContent: "flex-end" }}>
          <button className="btn btn--primary" type="submit" disabled={busy}>{m.withdraw.save}</button>
        </div>
      </form>
    </div>
  );
}
